import {useState} from "react";
import _ from "lodash";
import {useAtom, useAtomValue} from "jotai";
import {Button, ListItemIcon, Menu, MenuItem, Stack, Typography} from "@mui/material";
import MoreIcon from "@mui/icons-material/MoreVertOutlined";
import ArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {useActiveFarm} from "../../hooks/farmsHooks";
import {Farm} from "../../types/domain/farm";
import {routerCompanyIdAtom} from "../../store/routerStore";
import {FarmCreateDialog} from "../dialogs/FarmCreateDialog";
import {FarmIcon} from "../icons/FarmIcon";
import {EditIcon} from "../icons/EditIcon";
import {useAccessMarker} from "../../hooks/accessMarkerHooks";
import {ALL_ACCESS_FUNCTIONS} from "../../utils/accessControlUtils";
import {useTranslation} from "react-i18next";
import {activeYearAtom} from "../../store/activeYearAtom";
import {queryClient} from "../../utils/queryClient";

export const FarmHeader: React.FC = () => {
    const {t} = useTranslation();
    const companyId = useAtomValue(routerCompanyIdAtom);
    const farm = useActiveFarm();
    const access = useAccessMarker(ACCESS_FUNCTIONS);
    const [optionsAnchorEl, setOptionsAnchorEl] = useState<null | HTMLElement>(null);
    const [farmEditDialogOpened, setFarmEditDialogOpened] = useState<boolean>(false);
    const [activeYear] = useAtom(activeYearAtom);

    const onFarmEditDialogClose = (isSuccessful: boolean) => {
        setFarmEditDialogOpened(false);
        if (isSuccessful) {
            void queryClient.invalidateQueries({queryKey: ["companyFarms", companyId]});
        }
        setOptionsAnchorEl(null);
    };

    return (
        <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" gap="1.125rem">
                {farm && (
                    <FarmIcon
                        sx={{
                            width: "1.75rem",
                            height: "1.75rem",
                            stroke: "#5161C1",
                            strokeWidth: "2.2",
                        }}
                    />
                )}
                <Typography fontSize={30} lineHeight={1.27} fontWeight={700}>
                    {renderTitle(farm, activeYear)}
                </Typography>
            </Stack>
            {access.canEditCompanyFarms && (
                <Stack direction="row" alignItems="center" gap="0.5rem">
                    <Button
                        startIcon={<MoreIcon />}
                        size="large"
                        onClick={(e) => setOptionsAnchorEl(e.currentTarget)}
                        endIcon={<ArrowDownIcon />}
                    >
                        {t("insights.farm_header.options")}
                    </Button>
                    <Menu
                        id="farm-options-menu"
                        anchorEl={optionsAnchorEl}
                        open={!!optionsAnchorEl}
                        onClose={() => setOptionsAnchorEl(null)}
                    >
                        <MenuItem onClick={() => setFarmEditDialogOpened(true)}>
                            <ListItemIcon>
                                <EditIcon
                                    sx={{
                                        stroke: "#8F94A8",
                                        width: "1.125rem",
                                        height: "1.125rem",
                                    }}
                                />
                            </ListItemIcon>
                            {t("insights.farm_header.edit")}
                        </MenuItem>
                    </Menu>
                </Stack>
            )}

            {farmEditDialogOpened && companyId && (
                <FarmCreateDialog
                    mode="edit"
                    companyId={companyId}
                    selectedFarm={farm}
                    open={farmEditDialogOpened}
                    onClose={onFarmEditDialogClose}
                />
            )}
        </Stack>
    );
};

const renderTitle = (farmState: Farm | undefined, activeYear: number | undefined): React.ReactNode => {
    if (activeYear) {
        return activeYear.toString();
    } else if (farmState?.name) {
        return farmState.name;
    } else {
        return null;
    }
};

const ACCESS_FUNCTIONS = _.pick(ALL_ACCESS_FUNCTIONS, ["canEditCompanyFarms"]);
